import React, { useContext } from 'react';
import { ReportNavContext } from './reportNavContext';
import { useTranslation } from 'react-i18next';
import { NAV, SETTINGS } from './Sidebar';
import { bandForPage } from '../nav/activeBand';

/**
 * Navigation page-à-page (flèches ‹ › posées AU-DESSUS du titre), BORNÉE À LA
 * SECTION du menu. L'ordre suit exactement la barre de navigation (Sidebar > NAV) :
 * on cycle entre les pages d'une même bande, jamais d'une section à l'autre
 * (cyclique : la dernière ramène à la première). Une page seule dans sa section
 * (singleton) n'affiche pas de flèches.
 *
 * Source de vérité UNIQUE = `NAV` (+ `SETTINGS`) du Sidebar. Les sections sont
 * dérivées automatiquement : une bande = une section. Plus de liste parallèle à
 * maintenir à la main. Si le menu est filtré (entitlements), passer `nav` via
 * le contexte / getReportNav.
 */
export function buildNavSections(nav = NAV) {
  return [...nav, SETTINGS]
    .filter(n => n.type === 'band')
    // Une bande déclare ses entrées soit à plat (`items`), soit en groupes
    // (`groups`, cas des parcours d'étapes). Les groupes étaient ignorés : les
    // flèches ‹ › ne fonctionnaient donc pas dans Planification.
    .map(b => (b.groups ? b.groups.flatMap(g => g.items || []) : (b.items || []))
      .filter(i => i.key && !i.hidden));
}

/** @deprecated préférer buildNavSections(navFiltré) — conservé pour imports legacy */
export const NAV_SECTIONS = buildNavSections(NAV);

// Le contexte vit dans `reportNavContext.js` (même nom, ré-exporté ici).
export { ReportNavContext };

/**
 * Voisins { prev, next, go } cycliques dans la section contenant `page`.
 * Renvoie null si la page n'appartient à aucune section multi-pages.
 */
export function getReportNav(page, setPage, nav = NAV) {
  // Une section commune cycle dans la bande d'où l'on vient (2026-09-29).
  const band = bandForPage(nav, page);
  const own = band ? buildNavSections([band]).filter((s) => s.length) : [];
  for (const section of [...own, ...buildNavSections(nav)]) {
    const idx = section.findIndex(r => r.key === page);
    if (idx < 0) continue;
    if (section.length < 2) return null;     // singleton → pas de flèches
    const n = section.length;
    return {
      prev: section[(idx - 1 + n) % n],
      next: section[(idx + 1) % n],
      go: setPage,
    };
  }
  return null;
}

/**
 * Flèches ‹ › de navigation intra-section. À placer juste avant le <h1> du
 * titre, idéalement dans un conteneur `.page-header__title-row` (colonne) pour
 * qu'elles s'empilent au-dessus du titre. Rend null hors section multi-pages.
 */
export function ReportNav() {
  const { t } = useTranslation();
  const ctx = useContext(ReportNavContext);
  const nav = ctx ? getReportNav(ctx.page, ctx.setPage, ctx.nav || NAV) : null;
  if (!nav) return null;
  const prevLabel = nav.prev.labelKey ? t(nav.prev.labelKey) : nav.prev.label;
  const nextLabel = nav.next.labelKey ? t(nav.next.labelKey) : nav.next.label;
  return (
    <div className="page-header__nav-group">
      <button type="button" className="page-header__nav" title={`← ${prevLabel}`}
        onClick={() => nav.go(nav.prev.key)}>‹</button>
      <button type="button" className="page-header__nav" title={`${nextLabel} →`}
        onClick={() => nav.go(nav.next.key)}>›</button>
    </div>
  );
}
